// Clinical-priority axis. SSOT for the VALUES is device-engine
// config/categories/<device>.json -> finding_categories[*].clinical_tier,
// surfaced downstream as `category` (via demo-backend's request-time join).
// This file owns only the UI rank/label. Migrated in lockstep with the backend
// from the pre-2.0.0 `subcategory` taxonomy (high_priority/actionable/
// descriptive/silenced) to the 2.0.0 `clinical_tier` taxonomy (#489).
export type ClinicalTier = "acute" | "prompt_workup" | "routine_followup" | "noted";

export const PRIORITY_RANK: Record<ClinicalTier, number> = {
  acute: 3,
  prompt_workup: 2,
  routine_followup: 1,
  noted: 0,
};

const LABELS: Record<ClinicalTier, string> = {
  acute: "Acute",
  prompt_workup: "Actionable",
  routine_followup: "Routine",
  noted: "",
};

export function priorityLabel(category: string | null | undefined): string {
  return category == null ? "" : (LABELS[category as ClinicalTier] ?? "");
}

// Where findings are grouped by tier, every tier heads its group: a noted
// finding carries no tag of its own, but its group is still "Noted".
const HEADINGS: Record<ClinicalTier, string> = { ...LABELS, noted: "Noted" };

export function tierHeading(category: string | null | undefined): string {
  return category == null ? "" : (HEADINGS[category as ClinicalTier] ?? "");
}

// Indigo dot affordance: anything at or above "prompt_workup" priority carries it.
export function hasDot(category: string | null | undefined): boolean {
  return (
    category != null &&
    category in PRIORITY_RANK &&
    PRIORITY_RANK[category as ClinicalTier] >= PRIORITY_RANK.prompt_workup
  );
}

export interface PriorityFields {
  category?: string | null;
  advancement?: number | null;
  percentile?: number | null;
}

const rank = (c?: string | null) =>
  c && c in PRIORITY_RANK ? PRIORITY_RANK[c as ClinicalTier] : -1;

// Sort comparator: priority desc, then OP advancement desc, then percentile desc.
export function comparePriority(a: PriorityFields, b: PriorityFields): number {
  const r = rank(b.category) - rank(a.category);
  if (r !== 0) return r;
  const adv = (b.advancement ?? -1) - (a.advancement ?? -1);
  if (adv !== 0) return adv;
  return (b.percentile ?? 0) - (a.percentile ?? 0);
}

export interface UrgencyFields {
  urgencyScore?: number | null;
  urgencyPercentile?: number | null;
}

// Per-STUDY worklist row order (reporter/surfacer cohorts): OP advancement
// (`urgencyScore`) desc, then cohort percentile desc. Rows with no score sink
// to the bottom. Mirrors the per-finding axis of `comparePriority` at study
// granularity.
export function compareUrgency(a: UrgencyFields | undefined, b: UrgencyFields | undefined): number {
  const sa = a?.urgencyScore ?? null;
  const sb = b?.urgencyScore ?? null;
  if (sa === null && sb === null) return 0;
  if (sa === null) return 1;
  if (sb === null) return -1;
  if (sa !== sb) return sb - sa;
  return (b?.urgencyPercentile ?? 0) - (a?.urgencyPercentile ?? 0);
}
